import CompanyGoogleMap from "company/detail/components/CompanyGoogleMap";
import { convertToBankFormState, convertToFormState, convertToVenueFormState, BankFormState, VenueFormState, getCompanyFieldMetaData } from "shared/utils/fieldsHelper/companyFields";
import { bankDetailSection } from "company/sections/bankDetailSection";
import { contactDetailSection } from "company/sections/contactDetailSection";
import { financialDetailSection } from "company/sections/financialDetailSection";
import { companyMainSection } from "company/sections/main";
import { venueDetailsSection } from "company/sections/venueDetailSection";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { Section } from "components/MainSectionTemplate/types";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CompanyApi } from "queries/Company";
import { VenueSidePanelShowList } from "../SidePanelShowList";
import { type CompanySidePanelType } from "./CompanySidePanel";
import { useHasAnyTags } from "components/Tag/hooks";
import { useHasMultipleStatuses } from "components/Status/hooks";

interface CompanySidePanelDetailsProps {
  company: server.dto.CompanySidePanelData
  type: CompanySidePanelType
  refetch: () => Promise<void>
}

export const CompanySidePanelDetails = (props: CompanySidePanelDetailsProps) => {
  const [company, setCompany] = React.useState<server.dto.Company>(props.company.company);
  const [venueDetails, setVenueDetails] = React.useState<server.dto.VenueDetails>();
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(company, serverReference));
  const [venueFormState, setVenueFormState] = React.useState<VenueFormState>(convertToVenueFormState(venueDetails, serverReference));
  const [bankFormState, setBankFormState] = React.useState<BankFormState>(convertToBankFormState(company.bank));

  React.useEffect(() => {
    setCompany(props.company.company);
    setVenueDetails(undefined);
    setCompanyFormState(convertToFormState(props.company.company, serverReference));
    setVenueFormState(convertToVenueFormState(undefined, serverReference));
    setBankFormState(convertToBankFormState(props.company.company.bank));

    if (props.company.company.isVenue !== "no") {
      const syncVenue = async () => {
        const response = await window.CoreApi.Venue.GetVenue(props.company.company.companyId);
        if (response.data.hasErrors) {
          return;
        }

        setCompany(response.data.data.company);
        setVenueDetails(response.data.data.details);
        setCompanyFormState(convertToFormState(response.data.data.company, serverReference));
        setVenueFormState(convertToVenueFormState(response.data.data.details, serverReference));
        setBankFormState(convertToBankFormState(response.data.data.company.bank));
      }

      syncVenue();
    }
  }, [props.company]);

  const onValueChanged = async (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "profile") {
      setCompanyFormState(prevState => ({
        ...prevState,
        profile: value,
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setCompanyFormState(prevState => ({
        ...prevState,
        tags: value,
      }));
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "networkPath") {
      setCompanyFormState(prevState => ({
        ...prevState,
        networkPath: value,
      }));
    } else if (key === "additional") {
      setCompanyFormState(prevState => ({
        ...prevState,
        additional: value,
      }));
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    } else if (key === "financialRisk") {
      setCompanyFormState(prevState => ({
        ...prevState,
        financialRisk: value,
      }));
    } else if (key === "euVatNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        euVatNumber: value,
      }));
    } else if (key === "globalLocationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        globalLocationNumber: value,
      }));
    } else if (key === "fiscalNumber1") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber1: value,
      }));
    } else if (key === "fiscalNumber2") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber2: value,
      }));
    } else if (key === "businessRegistrationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        businessRegistrationNumber: value,
      }));
    } else if (key === "capacity") {
      setVenueFormState(prevState => ({
        ...prevState,
        capacity: value ?? 0,
      }));
    } else if (key === "ageRestriction") {
      setVenueFormState(prevState => ({
        ...prevState,
        ageRestriction: value,
      }));
    } else if (key === "category") {
      setVenueFormState(prevState => ({
        ...prevState,
        category: value,
      }));
    } else if (key === "stage") {
      setVenueFormState(prevState => ({
        ...prevState,
        stage: value,
      }));
    } else if (key === "vehicleAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        vehicleAccess: value,
      }));
    } else if (key === "parking") {
      setVenueFormState(prevState => ({
        ...prevState,
        parking: value,
      }));
    } else if (key === "gearAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        gearAccess: value,
      }));
    } else if (key === "power") {
      setVenueFormState(prevState => ({
        ...prevState,
        power: value,
      }));
    } else if (key === "lighting") {
      setVenueFormState(prevState => ({
        ...prevState,
        lighting: value,
      }));
    } else if (key === "dressingRooms") {
      setVenueFormState(prevState => ({
        ...prevState,
        dressingRooms: value,
      }));
    } else if (key === "noiseRestrictions") {
      setVenueFormState(prevState => ({
        ...prevState,
        noiseRestrictions: value,
      }));
    } else if (key === "doors") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: value
      }));
    } else if (key === "accountNumber") {
      setBankFormState(prevState => ({
        ...prevState,
        accountNumber: value,
      }));
    } else if (key === "beneficiaryName") {
      setBankFormState(prevState => ({
        ...prevState,
        beneficiaryName: value,
      }));
    } else if (key === "swiftCode") {
      setBankFormState(prevState => ({
        ...prevState,
        swiftCode: value,
      }));
    } else if (key === "sortCode") {
      setBankFormState(prevState => ({
        ...prevState,
        sortCode: value,
      }));
    } else if (key === "bankAddress") {
      setBankFormState(prevState => ({
        ...prevState,
        bankAddress: value,
      }));
    } else if (key === "internalId") {
      setCompanyFormState(prevState => ({
        ...prevState,
        internalId: value,
      }));
    } else if (key === "legalStructure") {
      setCompanyFormState(prevState => ({
        ...prevState,
        legalStructure: value,
      }));
    } else if (key === "expectPurchaseOrderNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        expectPurchaseOrderNumber: value,
      }));
    }
  }

  const queryClient = useQueryClient();

  const onSuccessfulUpdate = async () => {
    queryClient.invalidateQueries(CompanyApi.CanReceivePeppol.getCacheKey(company.companyId));
    await props.refetch();
  }

  const hasMultipleStatuses = useHasMultipleStatuses("company", company.clientId);
  const hasAnyTags = useHasAnyTags("company");
  const fieldMetaData = getCompanyFieldMetaData(
    site.currentEnvironment,
    serverReference,
    companyFormState,
    venueFormState,
    bankFormState,
    company.permissions,
    hasAnyTags,
    hasMultipleStatuses,
    false,
    false
  );

  const mainSection: Section = { ...companyMainSection(companyFormState, bankFormState, fieldMetaData, true), hideTitle: true };
  const deletedSection: Section = {
    key: "deleted",
    type: "custom",
    hidden: fieldMetaData.deleted.fields().hidden,
    render: () => <DeletedRow />,
  };
  const notValidatedSection: Section = {
    key: "isValidated",
    type: "custom",
    hidden: fieldMetaData.isValidated.fields().hidden,
    render: () => <NotValidatedRow
      canApprove={!fieldMetaData.isValidated.fields().editDisabled}
      sublabel={RESX.Planning.InputFromWebform}
      approveEndpoint={() => window.CoreApi.Company.Validate({
        companyId: companyFormState.companyId
      })}
    />,
  };

  const financialSection = financialDetailSection(companyFormState, bankFormState, fieldMetaData);
  const contactSection = contactDetailSection(companyFormState, bankFormState, fieldMetaData);
  const venueSection = venueDetailsSection(companyFormState, venueFormState, fieldMetaData);
  const bankSection = bankDetailSection(companyFormState, bankFormState, fieldMetaData);

  const sections = props.type === "venue"
    ? [
      deletedSection,
      notValidatedSection,
      mainSection,
      contactSection,
      venueSection,
    ]
    : [
      deletedSection,
      notValidatedSection,
      mainSection,
      financialSection,
      contactSection,
      venueSection,
      bankSection,
    ];

  return <SidePanelDetailSectionTemplate
    isInPageEditable={company.permissions.canModify}
    onSuccessfulUpdate={onSuccessfulUpdate}
    onValueChanged={onValueChanged}
    sections={sections}
    additionalContent={<>
      <ViewOnMap height={400}>
        <CompanyGoogleMap company={company} />
      </ViewOnMap>

      {props.type === "venue" && <VenueSidePanelShowList venueId={company.companyId} />}
    </>}
  />
}
